<script setup lang="ts">
const props = defineProps<{
  url?: string
  title?: string
  description?: string
  inputWrapperClasses?: string
  copyButtonClasses?: string
  socialShareButtonClasses?: string
}>()

const state = reactive({
  showingSuccessIndicator: false
})

const shareUrl = computed(() => {
  if (props.url) {
    return props.url
  }

  if (import.meta.env.SSR) return

  return window.location.href
})

const emailShareLink = computed(() => {
  let link = `mailto:`

  if (props.title) {
    link += `?subject=${encodeURIComponent(props.title)}`
  }

  let body = ''

  if (props.description) {
    body += props.description + '\n\n'
  }

  if (shareUrl.value) {
    body += shareUrl.value
  }

  link += `?body=${encodeURIComponent(body)}`

  return link
})

const facebookShareLink = computed(() => {
  let link = `https://www.facebook.com/sharer/sharer.php?hashtag=${encodeURIComponent('#riserapp')}`

  if (shareUrl.value) {
    link += `&u=${encodeURIComponent(shareUrl.value)}`
  }

  return link
})

const twitterShareLink = computed(() => {
  let link = `https://twitter.com/intent/tweet?hashtags=riserapp&via=riserblog`

  if (shareUrl.value) {
    link += `&url=${encodeURIComponent(shareUrl.value)}`
  }

  let text = ''

  if (props.title) {
    text += props.title + ' '
  }

  if (props.description) {
    text += props.description
  }

  link += `&text=${encodeURIComponent(text)}`

  return link
})

const copyUrl = () => {
  if (!shareUrl.value) return

  navigator.clipboard.writeText(shareUrl.value)

  state.showingSuccessIndicator = true

  setTimeout(() => {
    state.showingSuccessIndicator = false
  }, 2000)
}
</script>

<template>
  <div>
    <div>
      <p class="tw-mb-2 tw-text-sm">{{ $t('Copy link') }}</p>

      <div
        class="tw-mb-8 tw-flex tw-w-full tw-items-center tw-overflow-hidden tw-rounded-lg tw-border"
        :class="props.inputWrapperClasses"
      >
        <div class="tw-relative tw-flex-grow">
          <input
            class="normalize-style tw-w-full tw-bg-transparent tw-px-2 tw-py-1"
            type="text"
            :value="shareUrl"
            readonly
          />

          <div
            v-if="state.showingSuccessIndicator"
            class="tw-absolute tw-inset-0 tw-z-10 tw-content-center tw-px-2 tw-py-1 tw-text-center tw-text-sm tw-backdrop-blur"
          >
            {{ $t('Copied to Clipboard') }}
          </div>
        </div>

        <InputAppButton
          class="tw-h-full tw-bg-riser-blue tw-px-3 tw-py-2 tw-text-white"
          :class="props.copyButtonClasses"
          @click="copyUrl"
        >
          <FontAwesomeIcon icon="copy" />
        </InputAppButton>
      </div>

      <p class="tw-mb-2 tw-text-sm">{{ $t('or share on') }}&hellip;</p>

      <div class="tw-grid tw-grid-cols-3 tw-gap-2">
        <a
          :href="emailShareLink"
          target="_blank"
          class="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-rounded-md tw-border tw-px-2 tw-py-2 tw-text-center tw-shadow"
          :class="props.socialShareButtonClasses"
        >
          <FontAwesomeIcon icon="envelope" class="tw-text-2xl" />

          <span class="tw-text-sm">{{ $t('Email') }}</span>
        </a>

        <a
          :href="facebookShareLink"
          target="_blank"
          class="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-rounded-md tw-border tw-px-2 tw-py-2 tw-text-center tw-shadow"
          :class="props.socialShareButtonClasses"
        >
          <FontAwesomeIcon :icon="['fab', 'facebook-f']" class="tw-text-2xl" />

          <span class="tw-text-sm">Facebook</span>
        </a>

        <a
          :href="twitterShareLink"
          target="_blank"
          class="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-rounded-md tw-border tw-px-2 tw-py-2 tw-text-center tw-shadow"
          :class="props.socialShareButtonClasses"
        >
          <FontAwesomeIcon :icon="['fab', 'twitter']" class="tw-text-2xl" />

          <span class="tw-text-sm">Twitter</span>
        </a>
      </div>
    </div>
  </div>
</template>
